import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Heading from "@commons/Typography/Heading";
import Button from "@commons/Button";
import Text from "../Typography/Text";
import { twMerge } from "tailwind-merge";

type ConfirmationModalProps = {
  open: boolean;
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning" | "info";
};

function ConfirmationModal({
  open,
  title,
  message,
  onClose,
  onConfirm,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  variant = "warning",
}: ConfirmationModalProps) {
  if (!open) return null;

  const variantStyles = {
    danger: {
      icon: "text-red-600",
      bg: "bg-red-50",
      button: "danger" as const,
    },
    warning: {
      icon: "text-yellow-600",
      bg: "bg-yellow-50",
      button: "secondary" as const,
    },
    info: {
      icon: "text-blue-600",
      bg: "bg-blue-50",
      button: "primary" as const,
    },
  };

  const currentVariant = variantStyles[variant];

  return (
    <div
      data-testid="confirmation-modal"
      className="fixed inset-0 backdrop-opacity-80 bg-accent/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="max-w-md w-full bg-white rounded-lg shadow-xl flex flex-col"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <section className="relative p-4 flex items-center justify-end">
          <IconButton onClick={onClose} className="h-8 w-8" aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </section>

        <section className="px-6 pb-6 flex flex-col items-center text-center gap-4">
          <div className={twMerge("p-3 rounded-full", currentVariant.bg)}>
            <WarningAmberIcon
              className={twMerge("text-4xl", currentVariant.icon)}
            />
          </div>

          <Heading size="md" className="font-semibold">
            {title}
          </Heading>

          <Text size="md" className="text-gray-600">
            {message}
          </Text>
        </section>

        <section className="p-6 pt-2 flex gap-3">
          <Button
            customVariant="neutral"
            size="sm"
            onClick={onClose}
            className="flex-1"
          >
            <Text size="sm" className="font-roboto font-semibold">
              {cancelLabel}
            </Text>
          </Button>
          <Button
            customVariant={currentVariant.button}
            size="sm"
            onClick={onConfirm}
            className="flex-1"
          >
            <Text size="sm" className="font-roboto font-semibold">
              {confirmLabel}
            </Text>
          </Button>
        </section>
      </div>
    </div>
  );
}

export default ConfirmationModal;
